import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, combineLatestWith, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';
import * as fromActions from './actions';
import {
  AgrirouterAuthorizationService,
  AgrirouterService,
  AgrirouterTaskDataVehicleService,
  AgrirouterVehicleService,
} from '@services/api/sdf-agrirouter-api.service';
import { LoaderService } from '@services/loader.service';
import { loadedOrganizationsSubscriber, selectSelectedOrganizationId } from '@redux/index';
import {
  selectConnectionsNotOnboardedVehicles,
  selectConnectionsOnboardedVehicles,
  selectConnectionsReceivedFiles,
  selectConnectionsSelectedAccount,
  selectConnectionsSelectedVin,
  selectConnectionsSentFiles,
} from '@redux/modules/connections/index';

@Injectable()
export class ConnectionsEffects {

  constructor(
    private actions$: Actions,
    private store: Store,
    private agrirouterService: AgrirouterService,
    private agrirouterVehicleService: AgrirouterVehicleService,
    private agrirouterAuthorizationService: AgrirouterAuthorizationService,
    private agrirouterTaskDataVehicleService: AgrirouterTaskDataVehicleService,
    private loaderService: LoaderService,
  ) {
  }

  // Load Accounts
  loadAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadAccounts),
      tap(() => this.store.dispatch(fromActions.setLoadingAccounts({ value: true }))),
      switchMap((action) =>
        this.agrirouterService.getAgrirouterAccounts(action.organizationId).pipe(
          map((accounts) => fromActions.loadAccountsSuccess({ accounts })),
          catchError((error) => of(fromActions.loadAccountsFailure({ error }))),
        ),
      ),
    ),
  );

  loadAccountsSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(fromActions.loadAccountsSuccess),
        tap(() => this.store.dispatch(fromActions.setLoadingAccounts({ value: false }))),
      ),
    { dispatch: false },
  );

  loadAccountsFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(fromActions.loadAccountsFailure),
        tap(() => this.store.dispatch(fromActions.setLoadingAccounts({ value: false }))),
      ),
    { dispatch: false },
  );

  // Add Account
  addAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.addAccount),
      tap(() => this.loaderService.show()),
      switchMap((action) => {
        const redirectUrl = window.location.origin + window.location.pathname + '?openAddTractor=true';
        return this.agrirouterAuthorizationService
          .generateAuthorizationLink(action.organizationId, action.accountName, redirectUrl)
          .pipe(
            map((response: any) => fromActions.addAccountSuccess({ authUrl: response.redirect_url })),
            catchError(() => {
              this.loaderService.hide();
              return of(fromActions.addAccountFailure({ error: 'Failed to generate authorization link' }));
            }),
          );
      }),
    ),
  );

  addAccountSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(fromActions.addAccountSuccess),
        tap((action) => {
          this.loaderService.hide();
          window.open(action.authUrl, '_self');
        }),
      ),
    { dispatch: false },
  );

  // Remove Account
  removeAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.removeAccount),
      combineLatestWith(
        this.store.select(selectSelectedOrganizationId),
        loadedOrganizationsSubscriber(this.store),
      ),
      tap(() => this.loaderService.show()),
       switchMap(([action, selectedOrganizationId]) =>
         this.agrirouterAuthorizationService.revokeApplication(action.accountId, selectedOrganizationId).pipe(
           map(() => fromActions.removeAccountSuccess({ accountId: action.accountId })),
           catchError(() => {
             this.loaderService.hide();
             return of(fromActions.removeAccountFailure({ error: 'Failed to revoke application' }));
           }),
         ),
       ),
    ),
  );

  removeAccountSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(fromActions.removeAccountSuccess),
        tap(() => this.loaderService.hide()),
      ),
    { dispatch: false },
  );

  setOnboardedVehiclesFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setOnboardedVehiclesFilters),
    tap(() => {
      this.store.dispatch(fromActions.setOnboardedVehiclesPaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  // Load Onboarded Vehicles
  getOnboardedVehiclesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getOnboardedVehiclesList,
        fromActions.setOnboardedVehiclesPaginator),
      combineLatestWith(
        this.store.select(selectSelectedOrganizationId),
        loadedOrganizationsSubscriber(this.store),
      ),
      withLatestFrom(
        this.store.select(selectConnectionsSelectedAccount),
        this.store.select(selectConnectionsOnboardedVehicles),
      ),
      tap(() => this.store.dispatch(fromActions.setOnboardedVehiclesLoading({ isLoading: true }))),
      switchMap(async ([[_, selectedOrganizationId], account, vehicles]) => {
        const vehiclesData = await firstValueFrom(this.agrirouterVehicleService.getVehiclesOnboarded(
          selectedOrganizationId,
          account.agrirouterAccountId,
          vehicles.filters.qSearch,
          vehicles.paginator.page,
          vehicles.paginator.pageSize,
        ));
        return [
          fromActions.setOnboardedVehiclesList({ response: vehiclesData }),
          fromActions.setOnboardedVehiclesLoading({ isLoading: false }),
        ];
      }),
      catchError(() => of([
        fromActions.setOnboardedVehiclesList({ response: { data: [], totalCount: 0 } }),
        fromActions.setOnboardedVehiclesLoading({ isLoading: false }),
      ])),
      switchMap((actions) => [...actions]),
    ),
  );

  getNotOnboardedVehiclesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getNotOnboardedVehiclesList,
        fromActions.setNotOnboardedVehiclesPaginator),
      combineLatestWith(
        this.store.select(selectSelectedOrganizationId),
        loadedOrganizationsSubscriber(this.store),
      ),
      withLatestFrom(
        this.store.select(selectConnectionsNotOnboardedVehicles),
      ),
      tap(() => this.store.dispatch(fromActions.setNotOnboardedVehiclesLoading({ isLoading: true }))),
      switchMap(async ([[_, selectedOrganizationId], vehicles]) => {
        const vehiclesData = await firstValueFrom(this.agrirouterVehicleService.getVehiclesNotYetOnboarded(
          selectedOrganizationId,
          vehicles.paginator.page,
          vehicles.paginator.pageSize,
        ));
        return [
          fromActions.setNotOnboardedVehiclesList({ response: vehiclesData }),
          fromActions.setNotOnboardedVehiclesLoading({ isLoading: false }),
        ];
      }),
      catchError(() => of([
        fromActions.setNotOnboardedVehiclesList({ response: { data: [], totalCount: 0 } }),
        fromActions.setNotOnboardedVehiclesLoading({ isLoading: false }),
      ])),
      switchMap((actions) => [...actions]),
    ),
  );

  getSentFilesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getSentFilesList,
        fromActions.setSentFilesPaginator),
      combineLatestWith(
        this.store.select(selectSelectedOrganizationId),
        loadedOrganizationsSubscriber(this.store),
      ),
      withLatestFrom(
        this.store.select(selectConnectionsSelectedVin),
        this.store.select(selectConnectionsSelectedAccount),
        this.store.select(selectConnectionsSentFiles),
      ),
      tap(() => this.store.dispatch(fromActions.setSentFilesLoading({ isLoading: true }))),
      switchMap(async ([[_, selectedOrganizationId], vin, account, files]) => {
        const filesData = await firstValueFrom(this.agrirouterTaskDataVehicleService
          .getFilesFromAgrirouter(
            selectedOrganizationId,
            account.agrirouterAccountId,
            vin,
            files.paginator.page,
            files.paginator.pageSize,
          ));
        return [
          fromActions.setSentFilesList({ response: filesData }),
          fromActions.setSentFilesLoading({ isLoading: false }),
        ];
      }),
      catchError(() => of([
        fromActions.setSentFilesList({ response: { data: [], totalCount: 0 } }),
        fromActions.setSentFilesLoading({ isLoading: false }),
      ])),
      switchMap((actions) => [...actions]),
    ),
  );

  getReceivedFilesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getReceivedFilesList,
        fromActions.setReceivedFilesPaginator),
      combineLatestWith(
        this.store.select(selectSelectedOrganizationId),
        loadedOrganizationsSubscriber(this.store),
      ),
      withLatestFrom(
        this.store.select(selectConnectionsSelectedVin),
        this.store.select(selectConnectionsSelectedAccount),
        this.store.select(selectConnectionsReceivedFiles),
      ),
      tap(() => this.store.dispatch(fromActions.setReceivedFilesLoading({ isLoading: true }))),
      switchMap(async ([[_, selectedOrganizationId], vin, account, files]) => {
        const filesData = await firstValueFrom(this.agrirouterTaskDataVehicleService
          .getFilesToAgrirouter(
            selectedOrganizationId,
            account.agrirouterAccountId,
            vin,
            files.paginator.page,
            files.paginator.pageSize,
          ));
        return [
          fromActions.setReceivedFilesList({ response: filesData }),
          fromActions.setReceivedFilesLoading({ isLoading: false }),
        ];
      }),
      catchError(() => of([
        fromActions.setReceivedFilesList({ response: { data: [], totalCount: 0 } }),
        fromActions.setReceivedFilesLoading({ isLoading: false }),
      ])),
      switchMap((actions) => [...actions]),
    ),
  );

   // Onboard Vehicle
   onboardVehicle$ = createEffect(() =>
     this.actions$.pipe(
       ofType(fromActions.onboardVehicle),
       tap(() => this.loaderService.show()),
       switchMap((action) =>
         this.agrirouterService
           .onboardVcu({ vin: action.vin, agrirouterAccountId: action.agrirouterAccountId })
           .pipe(
             tap(() => this.loaderService.hide()),
             switchMap(() => [
               fromActions.getOnboardedVehiclesList(),
               fromActions.getNotOnboardedVehiclesList({ accountId: action.agrirouterAccountId }),
             ]),
             catchError((_) => {
               this.loaderService.hide();
               return of();
             }),
           ),
       ),
     ),
   );

   // Offboard Vehicle
   offboardVehicle$ = createEffect(() =>
     this.actions$.pipe(
       ofType(fromActions.offboardVehicle),
       tap(() => this.loaderService.show()),
       switchMap((action) =>
         this.agrirouterService
           .offboardVcu(action.organizationId, action.agrirouterAccountId, action.vin)
           .pipe(
             tap(() => this.loaderService.hide()),
             switchMap(() => [
               fromActions.getOnboardedVehiclesList(),
             ]),
             catchError((_) => {
               this.loaderService.hide();
               return of();
             }),
           ),
       ),
     ),
   );

  // Delete File
  deleteFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.deleteFile),
      tap(() => this.loaderService.show()),
      switchMap((action) =>
        this.agrirouterTaskDataVehicleService
          .deleteFileByUrl(action.organizationId, action.fileId)
          .pipe(
            map(() =>
              fromActions.deleteFileSuccess({
                fileId: action.fileId,
                direction: action.direction,
              }),
            ),
            catchError((error) => {
              this.loaderService.hide();
              return of(fromActions.deleteFileFailure({ error }));
            }),
          ),
      ),
    ),
  );

  deleteFileSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(fromActions.deleteFileSuccess),
        tap(() => this.loaderService.hide()),
      ),
    { dispatch: false },
  );

  // Download File
  downloadFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.downloadFile),
      tap(() => this.loaderService.show()),
      switchMap((action) =>
        this.agrirouterTaskDataVehicleService.getFileByUrl(action.fileId).pipe(
          map((response) => fromActions.downloadFileSuccess({ url: response.url })),
          catchError((error) => {
            this.loaderService.hide();
            return of(fromActions.downloadFileFailure({ error }));
          }),
        ),
      ),
    ),
  );

  downloadFileSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(fromActions.downloadFileSuccess),
        tap((action) => {
          this.loaderService.hide();
          window.open(action.url, '_blank');
        }),
      ),
    { dispatch: false },
  );
}

